/**
 * PARTS-PRINT-01 (Seth 2026-09-23: the "Printable sheet" printed the sidebar
 * and was "just overall … not printer friendly at all … fix it to make it
 * usable or scrap"). /parts/print?day=YYYY-MM-DD is the chrome-free pick sheet
 * for whoever stages the trucks: one block per tech, a box to tick for every
 * item, what it is for (customer · address · window) and where it gets picked
 * up. Black-and-white, letter portrait, a tech never splits across a page. It
 * reads the same list as Today → Parts Desk and the techs' phones
 * (usePickupDay), so paper and screen can't drift.
 *
 * Competitor frame: ServiceTitan and FieldEdge both print a pick ticket per
 * truck for the morning pull — a plain table, not the screen.
 */
import { useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { usePickupDay, visibleItems, grabList, type PickupItem, type PickupTech } from "./pickupList";

const isoOf = (d: Date) => d.toLocaleDateString("en-CA");
const addDays = (iso: string, n: number) => { const d = new Date(iso + "T12:00:00"); d.setDate(d.getDate() + n); return isoOf(d); };
const fmtDay = (iso: string) => new Date(iso + "T12:00:00").toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" });
const fmtTime = (iso: string) => new Date(iso).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });

const SOURCE_WORD: Record<PickupItem["source"], string> = {
  auto: "From the notes",
  ticket: "Arrived part",
  pickup: "Part pickup",
  office: "Added by the office",
};

function whereWords(i: PickupItem): string {
  if (i.whereLabel) return i.whereLabel;
  if (i.where === "office") return "At the office";
  if (i.where === "vendor") return "Vendor will-call";
  return "—";
}

export function PartsPrintView() {
  const [params, setParams] = useSearchParams();
  const today = isoOf(new Date());
  const raw = params.get("day") ?? "";
  const day = /^\d{4}-\d{2}-\d{2}$/.test(raw) ? raw : today;
  const { data, isLoading, isError } = usePickupDay(day);
  const techs = data ?? [];
  const withItems = techs.filter((t) => visibleItems(t).length > 0);
  const quiet = techs.filter((t) => visibleItems(t).length === 0);
  const total = withItems.reduce((n, t) => n + visibleItems(t).length, 0);
  const setDay = (d: string) => setParams({ day: d }, { replace: true });

  useEffect(() => { document.title = `Parts for the Day · ${fmtDay(day)}`; }, [day]);

  return (
    <div className="mx-auto max-w-[8.5in] bg-white p-6 text-slate-900 print:max-w-none print:p-0">
      <style>{`@media print {
        @page { size: letter portrait; margin: 0.5in; }
        html, body { background: #fff !important; }
        .no-print { display: none !important; }
        .tech-block { break-inside: avoid; page-break-inside: avoid; }
      }`}</style>

      {/* Screen-only bar: the day and the Print button. Never on the paper. */}
      <div className="no-print mb-5 flex flex-wrap items-center gap-2 rounded-xl border border-hairline bg-slate-50 px-3 py-2 text-[13px] text-slate-600">
        <span className="font-semibold text-slate-800">Parts pick sheet</span>
        <button type="button" onClick={() => setDay(today)} className={`rounded-full border px-2.5 py-0.5 text-[12px] font-semibold ${day === today ? "border-slate-900 bg-slate-900 text-white" : "border-hairline bg-white"}`}>Today</button>
        <button type="button" onClick={() => setDay(addDays(today, 1))} className={`rounded-full border px-2.5 py-0.5 text-[12px] font-semibold ${day === addDays(today, 1) ? "border-slate-900 bg-slate-900 text-white" : "border-hairline bg-white"}`}>Tomorrow</button>
        <input type="date" value={day} onChange={(e) => e.target.value && setDay(e.target.value)} className="h-7 rounded-md border border-hairline bg-white px-2 text-[12px]" />
        <span className="text-slate-400">One tech per block; tick each box as it goes on the shelf.</span>
        <button type="button" onClick={() => window.print()} className="ml-auto rounded-lg bg-slate-900 px-3 py-1.5 text-[12.5px] font-semibold text-white hover:bg-slate-800">Print</button>
      </div>

      <header className="mb-4 border-b-2 border-slate-900 pb-2">
        <h1 className="text-[22px] font-bold leading-tight">Parts for the Day — {fmtDay(day)}</h1>
        <p className="mt-0.5 text-[12px] text-slate-600">
          Sedgwick Heating · {withItems.length} tech{withItems.length === 1 ? "" : "s"} · {total} item{total === 1 ? "" : "s"} · printed {fmtTime(new Date().toISOString())}
        </p>
      </header>

      {isError ? (
        <p className="text-sm text-red-700">Couldn't build the pick list. Try again in a moment.</p>
      ) : isLoading ? (
        <p className="text-sm text-slate-500">Building the pick list…</p>
      ) : withItems.length === 0 ? (
        <p className="text-sm text-slate-600">No parts are called out for anyone on {fmtDay(day)}.</p>
      ) : (
        withItems.map((t) => <TechBlock key={t.empno} tech={t} />)
      )}

      {!isLoading && quiet.length > 0 && withItems.length > 0 && (
        <p className="mt-2 text-[11.5px] text-slate-500">Nothing on the list for: {quiet.map((t) => t.name).join(", ")}.</p>
      )}
      <p className="mt-4 text-[10px] text-slate-400">From Pegasus — the same list is on each tech's phone. Items added after printing won't be on this sheet.</p>
    </div>
  );
}

function TechBlock({ tech }: { tech: PickupTech }) {
  const items = visibleItems(tech);
  const grab = grabList(tech);
  const prepped = items.filter((i) => i.preppedAt).length;
  const first = tech.name.split(" ")[0] || tech.name;
  return (
    <section className="tech-block mb-6">
      <div className="flex items-baseline justify-between gap-3 border-b border-slate-900 pb-1">
        <h2 className="text-[17px] font-bold">{tech.name}</h2>
        <span className="text-[12px] text-slate-600">
          {tech.jobs.length} stop{tech.jobs.length === 1 ? "" : "s"} · {items.length} item{items.length === 1 ? "" : "s"}{prepped > 0 ? ` · ${prepped} already prepped` : ""}
        </span>
      </div>
      {grab.length > 0 && (
        <p className="mt-1.5 text-[12.5px]">
          <span className="font-semibold">Grab: </span>
          {grab.map((g) => `${g.n}× ${g.label}`).join("  ·  ")}
        </p>
      )}
      <table className="mt-1.5 w-full border-collapse text-[12.5px]">
        <thead>
          <tr className="text-left text-[10.5px] uppercase tracking-wide text-slate-500">
            <th className="w-8 py-1 font-semibold" aria-label="Done" />
            <th className="py-1 pr-3 font-semibold">Item</th>
            <th className="py-1 pr-3 font-semibold">For</th>
            <th className="w-[28%] py-1 font-semibold">Pick up</th>
          </tr>
        </thead>
        <tbody>
          {items.map((i) => (
            <tr key={i.key} className="border-t border-slate-300 align-top">
              <td className="py-1.5">
                <span className="inline-flex h-4 w-4 items-center justify-center border-[1.5px] border-slate-900 text-[11px] font-bold leading-none" aria-label={i.preppedAt ? "Prepped" : "Not prepped"}>
                  {i.preppedAt ? "✓" : ""}
                </span>
              </td>
              <td className="py-1.5 pr-3">
                <div className="font-semibold">{i.qty > 1 ? `${i.qty}× ` : ""}{i.label}</div>
                {i.note && <div className="text-[11.5px] text-slate-600">{i.note}</div>}
                <div className="text-[10.5px] text-slate-500">{SOURCE_WORD[i.source]}</div>
              </td>
              <td className="py-1.5 pr-3">
                {i.job ? (
                  <>
                    <div>{i.job.name}</div>
                    <div className="text-[11.5px] text-slate-600">
                      {[i.job.address, i.job.city].filter(Boolean).join(", ")}
                      {i.job.window ? ` · ${i.job.window}` : ""}
                    </div>
                  </>
                ) : (
                  <div className="text-slate-600">{first}'s truck</div>
                )}
              </td>
              <td className="py-1.5">
                <div>{whereWords(i)}</div>
                {i.pickedUpAt && <div className="text-[11px] text-slate-500">Picked up {fmtTime(i.pickedUpAt)}</div>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}
